import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';

type IntegrationFlag = `${string}Integration`;
type IntegrationControl = `${string}IntegrationControl`;
type OrgModeControl = `${string}OrgModeControl`;

/* eslint-disable @cspell/spellchecker */

export interface FeatureFlags {
  [key: IntegrationControl]: boolean;

  [key: IntegrationFlag]: boolean;

  [key: OrgModeControl]: boolean;

  searchAccessDiscovery: boolean;
  adminAnalytics: boolean;
  appFilterSuggestions: boolean;
  autocompleteGoogleSearch: boolean;
  browserExtensionSettings: boolean;
  customChatbot: boolean;
  dashAiDown: boolean;
  displayLearnableBotsFeedback: boolean;
  enableBrowserHistorySearch: boolean;
  fileTypeFilterNoticeThreshold: number;
  notionBeta: boolean;
  notionUserAuth: boolean;
  recentDocVisits: boolean;
  searchSemantic?:
    | false
    | {
        enabled: boolean;
      };
  scheduleDemoLink: string;
  enableServiceWorker: boolean;
  hideRelatedSearches: boolean;
  hideAskDashAiQueriesHomepage: boolean;
  enableHomepageConfigIcons: boolean;
  enterprisePricingEnabled: boolean;
  useChatWebsocket: boolean;
  dashAiReconnectAppsEnabled: boolean;
  earn500SidebarLink: boolean;
  openaiO1MiniLlmDisabled: boolean;
  openaiO1PreviewLlmDisabled: boolean;
  openaiO3MiniLlmDisabled: boolean;
  openaiChatGpt4LatestLlmDisabled: boolean;
  anthropicClaudeSonnetLlmDisabled: boolean;
  anthropicClaudeSonnet37LlmDisabled: boolean;
  anthropicClaudeSonnet37ThinkingLlmDisabled: boolean;
  anthropicClaudeOpusLlmDisabled: boolean;
  googleGemini15LlmDisabled: boolean;
  googleGemini20FlashLlmDisabled: boolean;
  googleGemini20FlashThinkingLlmDisabled: boolean;
  metaLlama70BLlmDisabled: boolean;
  deepseekR1LlmDisabled: boolean;
  enableAllUsersToAddBotConnections: boolean;
  workspaceSetupDisabled: boolean;
  selfServeDisabled: boolean;
  teamPlanMultiDomainEnabled: boolean;
  homepageSupportEnabled: boolean;
  apiKeysEnabled: boolean;
  hideAnalyticsQuestionsTab: boolean;
  conversationFilesEnabled: boolean;
  knockNotificationsEnabled: boolean;
  appSpecificFiltersEnabled: boolean;
  multiplayerEnabled: boolean;
  tokenProbsConfidenceScore: boolean;
  realtimeMultiplayer: boolean;
  newNotificationsExperience: boolean;
  publicTopics: boolean;
  xaiGroqBetaLlmDisabled: boolean;
  xaiGroq2LlmDisabled: boolean;
  simplerInviteExperience: boolean;
}

const defaults: FeatureFlags = {
  searchAccessDiscovery: false,
  adminAnalytics: false,
  appFilterSuggestions: false,
  autocompleteGoogleSearch: false,
  browserExtensionSettings: false,
  customChatbot: false,
  dashAiDown: false,
  displayLearnableBotsFeedback: false,
  enableBrowserHistorySearch: false,
  fileTypeFilterNoticeThreshold: 6,
  notionBeta: false,
  notionUserAuth: false,
  recentDocVisits: false,
  searchSemantic: false,
  scheduleDemoLink: 'https://calendly.com/dashworks-gtm/discover-dashworks-2',
  enableServiceWorker: false,
  hideRelatedSearches: false,
  hideAskDashAiQueriesHomepage: false,
  enableHomepageConfigIcons: false,
  enterprisePricingEnabled: false,
  useChatWebsocket: false,
  dashAiReconnectAppsEnabled: false,
  earn500SidebarLink: false,
  openaiO1MiniLlmDisabled: false,
  openaiO3MiniLlmDisabled: false,
  googleGemini20FlashLlmDisabled: false,
  googleGemini20FlashThinkingLlmDisabled: false,
  deepseekR1LlmDisabled: false,
  openaiO1PreviewLlmDisabled: false,
  openaiChatGpt4LatestLlmDisabled: false,
  anthropicClaudeSonnetLlmDisabled: false,
  anthropicClaudeSonnet37LlmDisabled: false,
  anthropicClaudeSonnet37ThinkingLlmDisabled: false,
  anthropicClaudeOpusLlmDisabled: false,
  googleGemini15LlmDisabled: false,
  tokenProbsConfidenceScore: false,
  metaLlama70BLlmDisabled: false,
  enableAllUsersToAddBotConnections: false,
  workspaceSetupDisabled: false,
  selfServeDisabled: false,
  teamPlanMultiDomainEnabled: false,
  homepageSupportEnabled: false,
  apiKeysEnabled: false,
  hideAnalyticsQuestionsTab: false,
  conversationFilesEnabled: false,
  knockNotificationsEnabled: false,
  appSpecificFiltersEnabled: false,
  multiplayerEnabled: false,
  realtimeMultiplayer: false,
  newNotificationsExperience: false,
  publicTopics: false,
  xaiGroqBetaLlmDisabled: false,
  xaiGroq2LlmDisabled: false,
  simplerInviteExperience: false,
};

export function useFlag<K extends keyof FeatureFlags>(
  name: K
): FeatureFlags[K] {
  return useFlags<FeatureFlags>()[name] ?? defaults[name];
}

export function useAllFlags(): Partial<FeatureFlags> {
  const rawFlags = useFlags<FeatureFlags>();
  return useMemo(
    () => ({
      ...defaults,
      ...rawFlags,
    }),
    [rawFlags]
  );
}
